import { computed } from 'nanostores';

import { clamp } from '@iconik-io/core/src/clamp';
import { roundToDecimals } from '@iconik-io/core/src/round_to_decimals';

import type { SyncClient } from '../../sync/client/sync_client.js';
import { syncAtom } from '../../sync/sync_atom.js';
import type { Point } from '../../types.js';
import {
  clampZoom,
  getRotationFittedSize,
} from '../../utils/transformations.js';
import type { createSizeStore } from './size.js';

const ZOOM_INCREMENT_SIZE = 0.25;

export function createTransformationsStore(
  size: ReturnType<typeof createSizeStore>,
  syncClient: SyncClient,
) {
  const $zoom = syncAtom<number>('zoom', 1, { syncClient });
  const $pan = syncAtom<Point>('pan', { x: 0.5, y: 0.5 }, { syncClient });
  const $rotation = syncAtom<number>('rotation', 0, { syncClient });

  const $fittedContentSize = computed(
    [size.$contentSize, size.$containerSize, $rotation],
    (contentSize, containerSize, rotation) =>
      getRotationFittedSize(contentSize, containerSize, rotation),
  );

  const $naturalZoom = computed(
    [size.$naturalSize, $fittedContentSize],
    (naturalSize, fittedContentSize): number =>
      naturalSize.width / fittedContentSize.width,
  );

  const $isZoomResetAvailable = computed(
    [$zoom],
    (currentZoom): boolean => currentZoom !== 1,
  );

  const $isZoomAtNaturalSize = computed(
    [$zoom, $naturalZoom],
    (currentZoom, naturalZoom): boolean =>
      roundToDecimals(currentZoom, 4) === roundToDecimals(naturalZoom, 4),
  );

  function setZoom(zoom: number) {
    const value = clampZoom(zoom);
    $zoom.set(value);
    if (value <= 1) {
      setPan({ x: 0.5, y: 0.5 });
    }
  }

  function zoomIn() {
    setZoom($zoom.get() + ZOOM_INCREMENT_SIZE);
  }

  function zoomOut() {
    setZoom($zoom.get() - ZOOM_INCREMENT_SIZE);
  }

  function setNaturalZoom() {
    const naturalZoom = $naturalZoom.get();
    if (naturalZoom > 0) {
      setZoom(naturalZoom);
    }
  }

  function setPan(pan: Point) {
    $pan.set({ x: clamp(pan.x, 0, 1), y: clamp(pan.y, 0, 1) });
  }

  const $flip = syncAtom<boolean>('flip', false, { syncClient });

  const $flop = syncAtom<boolean>('flop', false, { syncClient });

  function flip() {
    $flip.set(!$flip.get());
  }

  function flop() {
    $flop.set(!$flop.get());
  }

  function setFlip(value: boolean) {
    $flip.set(value);
  }

  function setFlop(value: boolean) {
    $flop.set(value);
  }

  /**
   * Rotating changes the size the content is fitted at, so a zoomed-in view would
   * otherwise jump to a different magnification. Scaling the zoom by the change in
   * fitted size keeps the same part of the content on screen at the same size
   */
  function applyRotation(degrees: number) {
    const currentRotation = $rotation.get();
    const newRotation = degrees < 0 ? degrees + 360 : degrees;

    if (newRotation !== currentRotation && $zoom.get() !== 1) {
      const contentSize = size.$contentSize.get();
      const containerSize = size.$containerSize.get();
      const currentWidth = getRotationFittedSize(
        contentSize,
        containerSize,
        currentRotation,
      ).width;
      const newWidth = getRotationFittedSize(
        contentSize,
        containerSize,
        newRotation,
      ).width;

      if (currentWidth > 0 && newWidth > 0) {
        setZoom(($zoom.get() * currentWidth) / newWidth);
      }
    }

    $rotation.set(newRotation);
  }

  function rotate(degrees: number) {
    applyRotation(($rotation.get() + degrees) % 360);
  }

  function setRotation(degrees: number) {
    applyRotation(degrees % 360);
  }

  return {
    $zoom,
    $naturalZoom,
    $fittedContentSize,
    $pan,
    $isZoomResetAvailable,
    $isZoomAtNaturalSize,
    setZoom,
    zoomIn,
    zoomOut,
    setPan,
    setNaturalZoom,
    $flip,
    $flop,
    flip,
    flop,
    setFlip,
    setFlop,
    $rotation,
    rotate,
    setRotation,
  };
}
